import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, orgID, useData, useSession } from '@yingbaba/api';
import type { FileSourceView, ProjectFile } from '@yingbaba/types';
import {
  Button,
  ErrorNotice,
  FileAttachment,
  Modal,
  Notice,
  QueryState,
  useToast,
  useUnsavedChanges,
} from '@yingbaba/ui';

type Props = { file: ProjectFile; write: boolean; onChanged: () => void };
export function ProjectFileSource(props: Props) {
  const session = useSession();
  const role = session.data?.organizations.find((o) => o.id === orgID())?.role || '';
  if (!props.file.source_file_id) return null;
  return (
    <SourceControl
      key={`${props.file.id}:${orgID()}:${session.data?.account.id}:${role}:${props.write}`}
      {...props}
    />
  );
}
function SourceControl({ file, write, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        查看素材来源
      </Button>
      {open && (
        <SourceDialog
          file={file}
          write={write}
          onChanged={onChanged}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
function SourceDialog({ file, write, onChanged, onClose }: Props & { onClose: () => void }) {
  const q = useData<FileSourceView>(`/files/${file.id}/source`);
  const [share, setShare] = useState(false);
  const [chosen, setChosen] = useState<ProjectFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState<{
    source: ProjectFile;
    version: number;
    key: string;
  } | null>(null);
  const pending = useRef(false),
    alive = useRef(true),
    controller = useRef<AbortController | null>(null);
  const organization = useRef(orgID());
  const qc = useQueryClient(),
    toast = useToast();
  const current = () => alive.current && orgID() === organization.current;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  useUnsavedChanges({
    dirty: busy,
    title: '素材版本正在保存',
    description: '离开后会停止等待。已提交的结果可在项目版本记录中核对。',
    continueLabel: '继续等待',
    discardLabel: '离开后再核对',
    discard: () => controller.current?.abort(),
  });
  const selected = attempt?.source || chosen || q.data?.latest;
  const versionConflict = error instanceof ApiError && error.code === 'FILE_VERSION_CHANGED';
  function refreshFiles() {
    void qc.invalidateQueries({
      predicate: (query) =>
        query.queryKey[1] === organization.current &&
        typeof query.queryKey[0] === 'string' &&
        (/\/files|\/file-library/.test(query.queryKey[0]) ||
          query.queryKey[0] === `/projects/${file.project_id}`),
    });
    onChanged();
  }
  async function update() {
    if (!write || !share || !selected || pending.current || (!attempt && !q.data?.can_update))
      return;
    const command = attempt || {
      source: selected,
      version: q.data!.file.version,
      key: crypto.randomUUID(),
    };
    setAttempt(command);
    pending.current = true;
    setBusy(true);
    setError(null);
    controller.current = new AbortController();
    try {
      await api<ProjectFile>(`/files/${file.id}/source-version`, {
        method: 'POST',
        signal: controller.current.signal,
        headers: { 'Idempotency-Key': command.key },
        body: JSON.stringify({
          source_file_id: command.source.id,
          expected_version: command.version,
          share_confirmed: true,
        }),
      });
      if (!current()) return;
      refreshFiles();
      toast('已保存为本项目的新版本，旧版本和推广中已选文件保持不变');
      onClose();
    } catch (e) {
      if (current()) setError(e);
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  }
  const canUpdate = write && (attempt || q.data?.can_update);
  return (
    <Modal
      open
      title="素材来源与版本"
      description="引用固定的文件版本，来源更新不会自动替换项目素材。"
      busy={busy}
      onOpenChange={(value) => {
        if (!value && !pending.current) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            返回项目
          </Button>
          {versionConflict ? (
            <Button
              onClick={() => {
                refreshFiles();
                onClose();
              }}
            >
              刷新项目文件
            </Button>
          ) : (
            canUpdate && (
              <Button loading={busy} disabled={!share} onClick={() => void update()}>
                {attempt ? '重试保存' : '更新项目版本'}
              </Button>
            )
          )}
        </>
      }
    >
      <ErrorNotice error={error} />
      <QueryState query={q}>
        {q.data && (
          <div className="file-source-comparison">
            <section aria-label="项目使用的版本" className="file-source-version">
              <h3>项目使用的版本 · V{q.data.file.version || 1}</h3>
              <FileAttachment
                name={q.data.file.name}
                mime={q.data.file.mime}
                size={q.data.file.size}
                previewPath={`/files/${q.data.file.id}/preview`}
                downloadPath={`/files/${q.data.file.id}/download`}
              />
              {q.data.file.version_note && (
                <p className="small muted">{q.data.file.version_note}</p>
              )}
            </section>
            {q.data.source && (
              <section aria-label="引用时的来源版本" className="file-source-version">
                <h3>引用时的来源 · V{q.data.source.version || 1}</h3>
                <p className="small muted">来源项目：{q.data.project_title}</p>
                <FileAttachment
                  name={q.data.source.name}
                  mime={q.data.source.mime}
                  size={q.data.source.size}
                  previewPath={`/files/${q.data.source.id}/preview`}
                  downloadPath={`/files/${q.data.source.id}/download`}
                />
              </section>
            )}
            {selected && selected.id !== q.data.source?.id && (
              <section
                aria-label="可选择的来源版本"
                className="file-source-version file-source-new"
              >
                <h3>
                  {attempt || chosen ? '本次选定的来源' : '来源最新版本'} · V{selected.version || 1}
                </h3>
                <FileAttachment
                  name={selected.name}
                  mime={selected.mime}
                  size={selected.size}
                  previewPath={`/files/${selected.id}/preview`}
                  downloadPath={`/files/${selected.id}/download`}
                />
                {selected.version_note && <p className="small muted">{selected.version_note}</p>}
              </section>
            )}
            <Notice>{q.data.reason}</Notice>
            {canUpdate && (
              <>
                <label className="checkbox-label file-source-consent">
                  <input
                    type="checkbox"
                    checked={share}
                    disabled={busy || !!attempt}
                    onChange={(e) => {
                      setChosen(e.target.checked ? q.data?.latest || null : null);
                      setShare(e.target.checked);
                    }}
                  />
                  我确认有权将这份来源版本共享给本项目当前及后续加入的成员。
                </label>
                <p className="small muted">
                  将新增一条项目文件版本，不重复上传。旧版本、创作需求中的凭证及推广已选素材继续使用原文件，需另行调整。
                </p>
              </>
            )}
            {attempt && !busy && !versionConflict && (
              <Button
                variant="secondary"
                onClick={() => {
                  setAttempt(null);
                  setChosen(null);
                  setShare(false);
                  setError(null);
                  void q.refetch();
                }}
              >
                重新核对来源与项目版本
              </Button>
            )}
          </div>
        )}
      </QueryState>
    </Modal>
  );
}
